import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, MapPin, Search, X } from "lucide-react";
import { ChoiceRow, Drawer, DrawerField, DrawerGroup, drawerInput } from "../Drawer";
import { ConfirmDialog } from "../crm/ui";
import { fetchApi } from "./access";
import { metaHeaders } from "../../lib/ads";
import { inr } from "../../lib/format";

/**
 * Instagram → a published post → Boost as ad.
 *
 * POST /api/ads/boost-post { adAccountId, integrationId, platform: "instagram",
 * post_id, daily_budget (₹, at least 100), days (1–30), targeting, name, launch }
 * makes an engagement campaign whose ad is the post itself and answers
 * { campaign_id, adset_id, creative_id, ad_id, status, starts, ends }. It is
 * created paused unless launch is true, because a live one spends money; the
 * price is confirmed before anything is sent. Audience basics only: places
 * (Meta cities, or all of India), ages and gender.
 */

const MIN_BUDGET = 100;
const START = ["Save it paused", "Start after Meta's review"];
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");

export function IGBoostDrawer({ post, integrationId, adAccountId, onClose, onBoosted }) {
  const [budget, setBudget] = useState(500);
  const [days, setDays] = useState(7);
  const [cities, setCities] = useState([]); // [{ key, name, region }]
  const [query, setQuery] = useState("");
  const [found, setFound] = useState([]);
  const [ageMin, setAgeMin] = useState(18);
  const [ageMax, setAgeMax] = useState(65);
  const [gender, setGender] = useState("All");
  const [start, setStart] = useState(START[0]);
  const [name, setName] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const launch = start === START[1];
  const total = Number(budget) * Number(days);
  const fieldErrors = {
    budget: !(Number(budget) >= MIN_BUDGET) ? `Meta needs at least ${inr(MIN_BUDGET)} a day for a boost.` : "",
    days: !(Number.isInteger(Number(days)) && days >= 1 && days <= 30) ? "Between 1 and 30 days." : "",
    ages: !(ageMin >= 18 && ageMax <= 65 && ageMin <= ageMax) ? "Ages 18 to 65, youngest first." : "",
  };
  const invalid = Object.values(fieldErrors).some(Boolean);

  // Places: Meta's cities, the same search as Create campaign.
  useEffect(() => {
    if (query.trim().length < 2) { setFound([]); return undefined; }
    let live = true;
    const id = setTimeout(async () => {
      const r = await fetchApi("/api/ads/targeting-search", { params: { q: query, type: "adgeolocation", location_types: '["city"]', limit: 8 } });
      if (live) setFound(r.ok ? (r.data?.data || []).filter((c) => c.type === "city" && !cities.some((x) => x.key === c.key)) : []);
    }, 250);
    return () => { live = false; clearTimeout(id); };
  }, [query, cities]);

  const targeting = () => ({
    geo_locations: cities.length ? { cities: cities.map((c) => ({ key: c.key, radius: 25, distance_unit: "kilometer" })) } : { countries: ["IN"] },
    age_min: Number(ageMin),
    age_max: Number(ageMax),
    ...(gender !== "All" ? { genders: [gender === "Men" ? 1 : 2] } : {}),
  });

  const boost = async () => {
    setSending(true);
    setError("");
    const r = await fetchApi("/api/ads/boost-post", {
      method: "post",
      headers: metaHeaders().headers,
      body: { adAccountId, integrationId, platform: "instagram", post_id: String(post.id), daily_budget: Number(budget), days: Number(days), targeting: targeting(), ...(name.trim() ? { name: name.trim() } : {}), launch },
    });
    setSending(false);
    setConfirming(false);
    if (!r.ok) {
      setError(r.data?.requires_user_token || r.data?.requires_reconnect ? "Connect Meta again in Settings → Integrations, then try again." : r.message || "Meta didn't take the boost. Nothing was spent; try again.");
      return;
    }
    setResult(r.data?.data || {});
    onBoosted?.(r.data?.data || {});
  };

  const placeLine = cities.length ? cities.map((c) => c.name).join(", ") : "All of India";
  const actions = result
    ? <Link to={`/ads/campaigns/${encodeURIComponent(result.campaign_id || "")}`} className="flex h-11 items-center rounded-full bg-ink px-5 text-small font-semibold text-paper hover:bg-ink/85">Open the campaign</Link>
    : <button type="button" onClick={() => setConfirming(true)} disabled={invalid || sending} className="h-11 rounded-full bg-sun px-5 text-small font-semibold text-ink hover:brightness-95 disabled:opacity-50">Review boost</button>;

  return <>
    <Drawer open={!!post} onClose={onClose} title={result ? "Post boosted" : "Boost as ad"} context="Instagram · Published post" closeLabel={result ? "Close" : "Cancel"} dirty={!result && !sending && (cities.length > 0 || !!name.trim())} actions={actions}>
      <div className="mb-5 flex gap-3 rounded-sm bg-mist p-3">
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xs bg-paper">{post?.photo ? <img src={post.photo} alt="" className="h-full w-full object-cover" /> : null}</div>
        <p className="line-clamp-3 min-w-0 text-small">{post?.caption || "Instagram post"}</p>
      </div>
      {result ? (
        <div role="status" className="flex flex-col gap-4">
          <p className="flex items-start gap-2 text-small"><Check size={17} strokeWidth={2} className="mt-0.5 shrink-0 text-success" /><span>{result.status === "ACTIVE" ? "The boost is on Meta. It starts once Meta has reviewed the ad, usually within a few hours." : "The boost is saved paused on Meta. Nothing is spent until you turn it on in Ad campaigns."}</span></p>
          <dl className="grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-2 text-small">
            <dt className="text-muted">Status</dt><dd>{result.status === "ACTIVE" ? "Active, in review" : "Paused"}</dd>
            <dt className="text-muted">Runs</dt><dd>{fmtDay(result.starts)} to {fmtDay(result.ends)}</dd>
            <dt className="text-muted">Budget</dt><dd>{inr(Number(budget))} a day · at most {inr(total)}</dd>
            <dt className="text-muted">Who sees it</dt><dd className="break-words">{placeLine} · {ageMin} to {ageMax} · {gender === "All" ? "Everyone" : gender}</dd>
            <dt className="text-muted">Campaign</dt><dd className="break-all">{result.campaign_id}</dd>
          </dl>
        </div>
      ) : <>
        {error && <p role="alert" className="mb-4 rounded-sm bg-danger-soft px-3 py-2 text-caption text-danger">{error}</p>}
        <DrawerGroup title="Budget" hint="Meta spends up to this a day, for the days you choose.">
          <div className="grid grid-cols-2 gap-3">
            <DrawerField label="Daily budget (₹)" htmlFor="boost-budget" error={fieldErrors.budget}><input id="boost-budget" type="number" min={MIN_BUDGET} step={50} value={budget} onChange={(e) => setBudget(e.target.value === "" ? "" : Number(e.target.value))} aria-invalid={!!fieldErrors.budget} className={`${drawerInput} tabular-nums`} /></DrawerField>
            <DrawerField label="Days" htmlFor="boost-days" error={fieldErrors.days}><input id="boost-days" type="number" min={1} max={30} value={days} onChange={(e) => setDays(e.target.value === "" ? "" : Number(e.target.value))} aria-invalid={!!fieldErrors.days} className={`${drawerInput} tabular-nums`} /></DrawerField>
          </div>
          <p className="text-small">{invalid ? "—" : <>At most <span className="font-semibold tabular-nums">{inr(total)}</span> in all</>}</p>
        </DrawerGroup>
        <DrawerGroup title="Who sees it">
          <DrawerField label="Places" htmlFor="boost-place" hint={cities.length ? "25 km around each city." : "Leave empty for all of India."}>
            <div className="relative">
              <div className="flex h-11 items-center gap-2 rounded-sm border border-line bg-paper px-3 focus-within:border-ink"><Search size={16} strokeWidth={1.75} className="text-muted" /><input id="boost-place" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search a city" className="h-full min-w-0 flex-1 bg-transparent text-small outline-none placeholder:text-muted/70" /></div>
              {!!query.trim() && <div className="absolute inset-x-0 top-full z-20 mt-1 max-h-56 overflow-auto rounded-sm bg-paper p-1 shadow-lift ring-1 ring-line">{found.map((c) => <button key={c.key} type="button" onClick={() => { setCities((all) => [...all, { key: c.key, name: c.name, region: c.region || "" }]); setQuery(""); }} className="block w-full rounded-xs px-3 py-2 text-left text-small hover:bg-mist">{c.name}{c.region ? ` · ${c.region}` : ""}</button>)}{!found.length && <p className="px-3 py-2 text-caption text-muted">No matching city.</p>}</div>}
            </div>
            {cities.length > 0 && <div className="flex flex-wrap gap-1.5">{cities.map((c) => <button key={c.key} type="button" onClick={() => setCities((all) => all.filter((x) => x.key !== c.key))} aria-label={`Remove ${c.name}`} className="flex items-center gap-1 rounded-full bg-ink px-3 py-1.5 text-caption font-medium text-paper"><MapPin size={12} strokeWidth={1.75} />{c.name}<X size={12} strokeWidth={1.75} /></button>)}</div>}
          </DrawerField>
          <DrawerField label="Ages" error={fieldErrors.ages}>
            <div className="flex items-center gap-2"><input type="number" min={18} max={65} value={ageMin} onChange={(e) => setAgeMin(Number(e.target.value))} aria-label="Youngest age" className={`${drawerInput} tabular-nums`} /><span className="text-small text-muted">to</span><input type="number" min={18} max={65} value={ageMax} onChange={(e) => setAgeMax(Number(e.target.value))} aria-label="Oldest age" className={`${drawerInput} tabular-nums`} /></div>
          </DrawerField>
          <DrawerField label="Gender"><ChoiceRow label="Gender" value={gender} options={["All", "Women", "Men"]} onChange={setGender} /></DrawerField>
        </DrawerGroup>
        <DrawerGroup title="Start">
          <ChoiceRow label="Start" value={start} options={START} onChange={setStart} />
          <p className="text-caption text-muted">{launch ? "It starts spending once Meta approves the ad." : "Saved paused, it spends nothing until you turn it on in Ad campaigns."}</p>
          <DrawerField label="Campaign name" htmlFor="boost-name" optional><input id="boost-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} placeholder="Boost Instagram post" className={drawerInput} /></DrawerField>
        </DrawerGroup>
      </>}
    </Drawer>
    {confirming && <ConfirmDialog
      title={launch ? "Boost this post?" : "Save the boost paused?"}
      body={launch
        ? <>Meta will spend up to <span className="font-semibold text-ink">{inr(Number(budget))} a day</span> for {days} {Number(days) === 1 ? "day" : "days"}, at most <span className="font-semibold text-ink">{inr(total)}</span>, showing the post to {placeLine.toLowerCase() === "all of india" ? "people across India" : `people in ${placeLine}`}. It starts after Meta reviews the ad.</>
        : <>Olli creates the campaign on Meta, paused: nothing is spent until you turn it on. Once on, it spends up to <span className="font-semibold text-ink">{inr(Number(budget))} a day</span> for {days} {Number(days) === 1 ? "day" : "days"}, at most <span className="font-semibold text-ink">{inr(total)}</span>.</>}
      confirmLabel={sending ? "Boosting…" : launch ? `Boost for ${inr(total)}` : "Save paused"}
      tone="positive"
      onCancel={() => { if (!sending) setConfirming(false); }}
      onConfirm={() => { if (!sending) boost(); }}
    />}
  </>;
}
